import { useEffect, useRef } from "react";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";

export type InretrnReturnType = "return" | "withdrawal";

declare global {
  interface Window {
    Inretrn?: {
      init: (config: {
        drToken: string;
        languageCode: string;
        returnType?: string;
      }) => void;
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "inretrn-returns": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

export interface InretrnWidgetContentProps {
  scriptUrl: string;
  token: string;
  returnType: InretrnReturnType;
}

export const InretrnWidgetContent = ({
  scriptUrl,
  token,
  returnType,
}: InretrnWidgetContentProps) => {
  const { currentLanguageCode } = useCountriesAndLanguages();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) {
      return;
    }

    const handleScriptLoad = () => {
      if (!window.Inretrn || initializedRef.current) {
        return;
      }

      initializedRef.current = true;

      const hashMatch = window.location.hash.slice(1);
      const resolvedToken = hashMatch || token;
      // returnType by default we show a combination of both depending on the return policy settings. Possible values are "return" and "withdrawal".
      window.Inretrn.init({
        drToken: resolvedToken,
        languageCode: currentLanguageCode,
        ...(returnType !== "return" && { returnType }),
      });
    };

    if (window.Inretrn) {
      handleScriptLoad();
      return;
    }

    const existingScript = document.getElementById("inretrn-script");
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = "inretrn-script";
    script.src = scriptUrl;
    script.async = true;
    script.setAttribute("data-cookieconsent", "ignore");
    script.addEventListener("load", handleScriptLoad, { once: true });
    document.body.appendChild(script);
  }, [currentLanguageCode, returnType, scriptUrl, token]);

  return (
    <div className="w-full flex justify-center items-center flex-col mb-20 -mt-4 lg:-mt-10">
      <div className="bg-white pt-4 rounded-lg w-full flex justify-center lg:w-full [&>iframe]:h-[800px] [&>iframe]:w-full">
        <inretrn-returns className="inretrn-widget w-full h-full flex justify-center py-10" />
      </div>
    </div>
  );
};
